(() => {
  if (customElements.get('azr-pack-builder')) return;

  class AzrPackBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.limit = Number(this.dataset.limit || 6);
      this.allowDuplicates = this.dataset.allowDuplicates === 'true';
      this.selected = Array(this.limit).fill(null);
      this.activeSlot = null;
      this.activeFilter = 'all';

      this.modal = this.querySelector('[data-azr-modal]');
      this.openButtons = this.querySelectorAll('[data-azr-open]');
      this.closeButtons = this.querySelectorAll('[data-azr-close]');
      this.slots = Array.from(this.querySelectorAll('[data-azr-slot]'));
      this.cards = Array.from(this.querySelectorAll('[data-azr-perfume]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-azr-filter]'));
      this.search = this.querySelector('[data-azr-search]');
      this.count = this.querySelector('[data-azr-count]');
      this.progress = this.querySelector('[data-azr-progress]');
      this.submitButton = this.querySelector('[data-azr-submit]');
      this.submitText = this.querySelector('[data-azr-submit-text]');
      this.noResults = this.querySelector('[data-azr-no-results]');
      this.propertyInputs = Array.from(this.querySelectorAll('[data-azr-property]'));
      this.summaryInput = this.querySelector('[data-azr-summary]');

      this.readyLabel = this.submitButton?.dataset.readyLabel || 'Add my pack to cart';
      this.incompleteTemplate = this.submitButton?.dataset.incompleteTemplate || 'Choose [remaining] more';
      this.emptySlotTemplate = this.dataset.emptySlotTemplate || 'Choose perfume [number]';
      this.filledSlotTemplate = this.dataset.filledSlotTemplate || '[name]. Click to replace.';

      this.bindEvents();
      this.render();
    }

    bindEvents() {
      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const slotIndex = button.dataset.slotIndex;
          this.activeSlot = slotIndex !== undefined && slotIndex !== '' ? Number(slotIndex) : this.firstEmptySlot();
          this.openModal();
        });
      });

      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeModal()));

      this.slots.forEach((slot, index) => {
        slot.addEventListener('click', (event) => {
          if (event.target.closest('[data-azr-remove]')) return;
          this.activeSlot = index;
          this.openModal();
        });

        const removeButton = slot.querySelector('[data-azr-remove]');
        removeButton?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.selected[index] = null;
          this.render();
        });
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.selectPerfume(card));
      });

      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.activeFilter = this.normalizeGender(button.getAttribute('data-azr-filter') || 'all');
          this.filterButtons.forEach((item) => {
            const isActive = item === button;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
          this.applyFilters(true);
        });
      });

      this.search?.addEventListener('input', () => this.applyFilters(true));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.modal && !this.modal.hidden) this.closeModal();
      });

      this.querySelector('form')?.addEventListener('submit', (event) => {
        if (this.selected.filter(Boolean).length !== this.limit) {
          event.preventDefault();
          this.openModal();
        }
      });
    }

    firstEmptySlot() {
      const index = this.selected.findIndex((item) => item === null);
      return index === -1 ? 0 : index;
    }

    selectPerfume(card) {
      if (card.dataset.available === 'false' || card.disabled) return;

      const perfume = {
        id: card.dataset.id || '',
        productId: card.dataset.productId || '',
        variantId: card.dataset.variantId || '',
        sku: card.dataset.sku || '',
        name: card.dataset.name || '',
        inspired: card.dataset.inspired || '',
        gender: card.dataset.gender || '',
        family: card.dataset.family || '',
        notes: card.dataset.notes || '',
        image: card.dataset.image || ''
      };

      let targetIndex = Number.isInteger(this.activeSlot) ? this.activeSlot : this.firstEmptySlot();
      if (targetIndex < 0 || targetIndex >= this.limit) targetIndex = this.firstEmptySlot();

      if (!this.allowDuplicates) {
        const existingIndex = this.selected.findIndex((item, index) => item?.id === perfume.id && index !== targetIndex);
        if (existingIndex !== -1) return;
      }

      this.selected[targetIndex] = perfume;
      this.activeSlot = this.firstEmptySlot();
      this.render();

      if (this.selected.every(Boolean)) {
        this.closeModal();
      }
    }

    render() {
      this.slots.forEach((slot, index) => {
        const item = this.selected[index];
        const image = slot.querySelector('[data-azr-slot-image]');
        const name = slot.querySelector('[data-azr-slot-name]');

        slot.dataset.filled = item ? 'true' : 'false';

        const emptySlotLabel = this.emptySlotTemplate.includes('[number]')
          ? this.emptySlotTemplate.replace('[number]', String(index + 1))
          : `${this.emptySlotTemplate} ${index + 1}`;
        const filledSlotLabel = this.filledSlotTemplate.includes('[name]')
          ? this.filledSlotTemplate.replace('[name]', item?.name || '')
          : `${item?.name || ''}. ${this.filledSlotTemplate}`;

        slot.setAttribute('aria-label', item ? filledSlotLabel : emptySlotLabel);

        if (item) {
          if (image) {
            image.src = item.image || '';
            image.alt = item.name;
            image.hidden = !item.image;
          }
          if (name) name.textContent = item.name;
        } else {
          if (image) {
            image.removeAttribute('src');
            image.alt = '';
            image.hidden = true;
          }
          if (name) name.textContent = '';
        }
      });

      const selectedCount = this.selected.filter(Boolean).length;
      const remaining = this.limit - selectedCount;

      if (this.count) this.count.textContent = `${selectedCount}/${this.limit}`;
      if (this.progress) this.progress.style.width = `${(selectedCount / this.limit) * 100}%`;

      this.propertyInputs.forEach((input, index) => {
        const item = this.selected[index];
        input.value = item ? this.propertyValue(item) : '';
        input.disabled = !item;
      });

      if (this.summaryInput) {
        this.summaryInput.value = this.selected.filter(Boolean).map((item) => item.name).join(' | ');
      }

      if (this.submitButton) {
        const ready = selectedCount === this.limit && this.submitButton.dataset.productAvailable !== 'false';
        this.submitButton.disabled = !ready;
        if (this.submitText) {
          this.submitText.textContent = ready
            ? this.readyLabel
            : this.incompleteTemplate.replace('[remaining]', String(remaining));
        }
      }

      this.updateCardStates();
      this.applyFilters();
    }

    propertyValue(item) {
      const parts = [item.sku ? `${item.sku} — ${item.name}` : item.name];
      if (item.inspired) parts.push(`Inspired by ${item.inspired}`);
      if (item.family) parts.push(item.family);
      return parts.join(' — ');
    }

    updateCardStates() {
      this.cards.forEach((card) => {
        const count = this.selected.filter((item) => item?.id === card.dataset.id).length;
        const badge = card.querySelector('[data-azr-selected-count]');
        const available = card.dataset.available !== 'false';
        const duplicateDisabled = !this.allowDuplicates && count > 0 && this.selected[this.activeSlot]?.id !== card.dataset.id;
        card.classList.toggle('is-selected', count > 0);
        card.classList.toggle('is-sold-out', !available);
        if (badge) badge.textContent = this.allowDuplicates ? String(count) : '✓';
        card.disabled = !available || duplicateDisabled;
        card.setAttribute('aria-disabled', card.disabled ? 'true' : 'false');
      });
    }

    normalizeGender(value) {
      const gender = String(value || '').trim().toLowerCase();

      if (['women', 'woman', 'female', 'femme', 'for her', 'her'].includes(gender)) return 'women';
      if (['men', 'man', 'male', 'homme', 'for him', 'him'].includes(gender)) return 'men';
      if (['unisex', 'mixed'].includes(gender)) return 'unisex';
      return gender === 'all' ? 'all' : gender;
    }

    applyFilters(resetScroll = false) {
      const query = (this.search?.value || '').trim().toLowerCase();
      const activeFilter = this.normalizeGender(this.activeFilter || 'all');
      let visible = 0;

      this.cards.forEach((card) => {
        const gender = this.normalizeGender(card.getAttribute('data-gender') || '');
        const searchable = [
          card.getAttribute('data-name') || '',
          card.getAttribute('data-inspired') || '',
          card.getAttribute('data-family') || '',
          card.getAttribute('data-notes') || '',
          gender
        ].join(' ').toLowerCase();

        // For Her shows only women, For Him shows only men.
        // Unisex perfumes remain available under All.
        const filterMatch = activeFilter === 'all' || gender === activeFilter;
        const searchMatch = !query || searchable.includes(query);
        const show = filterMatch && searchMatch;

        card.hidden = !show;
        card.classList.toggle('is-filtered-out', !show);
        card.setAttribute('aria-hidden', show ? 'false' : 'true');

        if (show) visible += 1;
      });

      if (resetScroll) {
        const grid = this.querySelector('.azr-pack-builder__grid');
        if (grid) grid.scrollTop = 0;
      }

      this.noResults?.classList.toggle('is-visible', visible === 0);
    }

    openModal() {
      if (!this.modal || this.cards.length === 0) return;
      this.updateCardStates();
      this.modal.hidden = false;
      document.body.classList.add('azr-pack-modal-open');
      window.setTimeout(() => this.search?.focus(), 50);
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.classList.remove('azr-pack-modal-open');
    }
  }

  customElements.define('azr-pack-builder', AzrPackBuilder);
})();
